import React, { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CheckIcon,
  XMarkIcon,
  UsersIcon,
  CheckCircleIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import DropdownOptionsService from '../services/dropdownOptionsService';
import EnhancedLeadAssignmentModal from './EnhancedLeadAssignmentModal';
import EnquiryPurposeModal from './EnquiryPurposeModal';
import CustomerProfileModal from './CustomerProfileModal';
import { useFormFields } from '../hooks/useFormFields';
import api from '../utils/axios';

// Helper function to get emoji for status
const getStatusEmoji = (status) => {
  const emojiMap = {
    'new': '🆕',
    'contacted': '📞',
    'qualified': '✅',
    'proposal_sent': '📋',
    'negotiation': '🤝',
    'follow_up': '🔄',
    'demo_scheduled': '🎯',
    'quote_sent': '💰',
    'closed_won': '🎉',
    'closed_lost': '❌',
    'on_hold': '⏸️',
    'not_interested': '🚫'
  };
  return emojiMap[status] || '📋';
};

const LeadList = ({ leads, onEdit, onDelete, onStatusChange, onBulkDelete, onGenerateQuotation, onBranchAssignment, formFields: propFormFields, leadTypeFilter = 'tour_booking' }) => {
  const navigate = useNavigate();

  // Form fields hook
  const { fields: hookFormFields } = useFormFields();

  // Use prop formFields if available, otherwise use hook formFields
  const formFields = propFormFields || hookFormFields;

  // Assignment states
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatusOptions, setLoadingStatusOptions] = useState(true);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);

  // Lead status states
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);

  // Enquiry Purpose Modal state
  const [selectedLeadForPurpose, setSelectedLeadForPurpose] = useState(null);

  // Assignment Modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  // Customer Profile Modal state
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Assignment complete handler
  const onAssignmentComplete = () => {
    setShowAssignmentModal(false);
    // Refresh leads or handle assignment completion
  };

  // Load status options from dropdown options API
  useEffect(() => {
    const loadStatusOptions = async () => {
      console.log('🔄 LeadList: Loading status options...');
      setLoadingStatusOptions(true);
      const result = await DropdownOptionsService.getOptionsByCategory('status');
      console.log('📊 LeadList: Status options result:', result);
      console.log('🔍 LeadList: Status options data structure:', JSON.stringify(result.data, null, 2));
      if (result.success && result.data && result.data.length > 0) {
        console.log('✅ LeadList: Status options loaded:', result.data);
        console.log('🏷️ LeadList: First option structure:', result.data[0]);
        setStatusOptions(result.data);
      } else {
        console.warn('⚠️ LeadList: No status options found. Please add statuses in Dropdown Options.');
        // Empty array - user needs to add statuses in Dropdown Options
        setStatusOptions([
          { _id: 'default', label: 'New', value: 'new' }
        ]);
      }
      setLoadingStatusOptions(false);
    };

    loadStatusOptions();
  }, []);

  const getStatusColor = (status) => {
    // Find the status in our dynamic statuses
    const statusObj = leadStatuses.find(s => s.name === status);
    if (statusObj) {
      return `bg-${statusObj.color}-100 text-${statusObj.color}-800`;
    }

    // Fallback to hardcoded colors for backward compatibility
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'qualified': return 'bg-indigo-100 text-indigo-800';
      case 'proposal': return 'bg-blue-100 text-blue-800';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'Quoted':
      case 'quoted': return 'bg-green-100 text-green-800 font-bold';
      case 'Accepted':
      case 'accepted': return 'bg-emerald-100 text-emerald-800 font-bold';
      case 'Rejected':
      case 'rejected': return 'bg-red-100 text-red-800 font-bold';
      case 'closed_won': return 'bg-green-100 text-green-800';
      case 'closed_lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAvailableStatusTransitions = (currentStatus) => {
    const currentStatusObj = leadStatuses.find(s => s.name === currentStatus);

    if (currentStatusObj && currentStatusObj.canTransitionTo && currentStatusObj.canTransitionTo.length > 0) {
      // Return statuses that current status can transition to, plus current status
      // canTransitionTo contains status names, not ObjectIds
      const availableStatuses = leadStatuses.filter(status =>
        currentStatusObj.canTransitionTo.includes(status.name) || status.name === currentStatus
      );
      return availableStatuses;
    }
    // If no transitions defined, allow all statuses
    return leadStatuses;
  };

  const getSourceLabel = (source) => {
    const sourceMap = {
      'website': '🌐 Website',
      'instagram': '📷 Instagram',
      'facebook': '📘 Facebook',
      'whatsapp': '💬 WhatsApp',
      'linkedin': '💼 LinkedIn',
      'twitter': '🐦 Twitter',
      'youtube': '📺 YouTube',
      'referral': '👥 Referral',
      'email_campaign': '📧 Email Campaign',
      'phone_call': '📞 Phone Call',
      'trade_show': '🏢 Trade Show',
      'advertisement': '📢 Advertisement',
      'other': '❓ Other'
    };
    return sourceMap[source] || `📍 ${source}`;
  };

  const getSourceColor = (source) => {
    const colorMap = {
      'website': 'bg-green-100 text-green-800',
      'instagram': 'bg-pink-100 text-pink-800',
      'facebook': 'bg-blue-100 text-blue-800',
      'whatsapp': 'bg-green-100 text-green-800',
      'linkedin': 'bg-indigo-100 text-indigo-800',
      'twitter': 'bg-sky-100 text-sky-800',
      'youtube': 'bg-red-100 text-red-800',
      'referral': 'bg-purple-100 text-purple-800',
      'email_campaign': 'bg-orange-100 text-orange-800',
      'phone_call': 'bg-teal-100 text-teal-800',
      'trade_show': 'bg-gray-100 text-gray-800',
      'advertisement': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colorMap[source] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Bulk selection functions
  const isLeadSelected = (lead) => {
    return selectedLeads.some(selected => (selected._id || selected.id) === (lead._id || lead.id));
  };

  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev => {
      const isSelected = prev.some(selected => (selected._id || selected.id) === (lead._id || lead.id));
      if (isSelected) {
        return prev.filter(selected => (selected._id || selected.id) !== (lead._id || lead.id));
      } else {
        return [...prev, lead];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads([...leads]);
    }
  };

  const handleSingleAssign = (lead) => {
    setSelectedLeads([lead]);
    setShowAssignmentModal(true);
  };

  const handleBulkAssign = () => {
    if (selectedLeads.length > 0) {
      setShowAssignmentModal(true);
    }
  };

  const handleAssignmentComplete = (assignmentData) => {
    if (onAssignmentComplete) {
      onAssignmentComplete(assignmentData);
    }
    setShowAssignmentModal(false);
    setSelectedLeads([]);
  };

  if (leads.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
        <div className="text-gray-400 mb-4">
          <BuildingOfficeIcon className="h-16 w-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
        <p className="text-gray-500">Get started by adding your first lead.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
      {/* Header with bulk actions */}
      <div className="px-8 py-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>

                <p className="text-sm text-gray-600">
                  {leads.length} total leads
                </p>
              </div>
            </div>

            {/* Bulk Select Toggle */}
            <button
              onClick={() => {
                setBulkSelectMode(!bulkSelectMode);
                if (bulkSelectMode) {
                  setSelectedLeads([]);
                }
              }}
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-all duration-200 shadow-sm ${bulkSelectMode
                ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 shadow-blue-100'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:shadow-md'
                }`}
            >
              <UsersIcon className="h-4 w-4 mr-2" />
              {bulkSelectMode ? 'Exit Bulk Select' : 'Bulk Select'}
            </button>

            {/* Selected count */}
            {bulkSelectMode && selectedLeads.length > 0 && (
              <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 shadow-sm">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {selectedLeads.length} selected
              </div>
            )}
          </div>

          {/* Bulk Actions */}
          {bulkSelectMode && selectedLeads.length > 0 && (
            <div className="mt-3 sm:mt-0 flex items-center space-x-2">
              <button
                onClick={handleBulkAssign}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Assign to Employee ({selectedLeads.length})
              </button>
              {onBranchAssignment && (
                <button
                  onClick={() => onBranchAssignment(selectedLeads.map(id => leads.find(l => l._id === id)))}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  🏢 Assign to Branch ({selectedLeads.length})
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border-collapse">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              {bulkSelectMode && (
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={toggleSelectAll}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-md shadow-sm"
                  />
                </th>
              )}

              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <span>Sr No</span>
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-4 w-4" />
                  <span>Customer Name</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <div className="flex items-center space-x-2">
                  <PhoneIcon className="h-4 w-4" />
                  <span>Contact</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>Email</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <div className="flex items-center space-x-2">
                  <span>🔧</span>
                  <span>Service Type</span>
                </div>
              </th>
              {leadTypeFilter !== 'other_services' && (
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                  <div className="flex items-center space-x-2">
                    <span>🎒</span>
                    <span>Tour Package</span>
                  </div>
                </th>
              )}
              {leadTypeFilter !== 'other_services' && (
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                  <div className="flex items-center space-x-2">
                    <span>📅</span>
                    <span>Travel Date</span>
                  </div>
                </th>
              )}
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <div className="flex items-center space-x-2">
                  <span>👤</span>
                  <span>Added By</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide border-r border-gray-300">
                <span>Status</span>
              </th>
              <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wide">
                <span>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {leads.map((lead, index) => (
              <tr key={lead._id || lead.id} className={`transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-sm ${isLeadSelected(lead) ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 shadow-md' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                }`}>
                {bulkSelectMode && (
                  <td className="px-6 py-5 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={isLeadSelected(lead)}
                      onChange={() => toggleLeadSelection(lead)}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-md shadow-sm transition-all duration-200"
                    />
                  </td>
                )}
                {/* Serial Number */}
                <td className="px-6 py-5 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full">
                    <span className="text-sm font-bold text-blue-800">
                      {lead.serialNumber || (leads.indexOf(lead) + 1)}
                    </span>
                  </div>
                </td>

                {/* Customer Name */}
                <td className="px-6 py-5 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-200"
                          onClick={() => {
                            console.log('Opening customer profile:', lead._id || lead.id);
                            setSelectedLeadId(lead._id || lead.id);
                            setShowCustomerProfile(true);
                          }}
                          title="Click to view lead profile">
                          {lead.name || lead.customerName || '-'}
                        </span>
                        {/* Lead Type Badge */}
                        {lead.leadType === 'tour_booking' ? (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                            🏖️ Tour
                          </span>
                        ) : lead.leadType === 'other_services' ? (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full">
                            📋 Service
                          </span>
                        ) : null}

                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span>Lead ID: {(lead._id || lead.id)?.slice(-6)}</span>
                        {lead.quotationCount > 0 && (
                          <>
                            <span className="text-green-600 font-medium">
                              • {lead.quotationCount} {lead.quotationCount === 1 ? 'Quotation' : 'Quotations'}
                            </span>
                            {lead.lastQuotationDate && (
                              <span className="text-gray-400">
                                • Last: {new Date(lead.lastQuotationDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Contact Number */}
                <td className="px-6 py-5 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center space-x-2">
                    {(lead.phone || lead.contactNumber) ? (
                      <>
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <PhoneIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <a href={`tel:${lead.phone || lead.contactNumber}`} className="text-sm font-medium text-green-600 hover:text-green-800 transition-colors duration-200">
                          {lead.phone || lead.contactNumber}
                        </a>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No contact</span>
                    )}
                  </div>
                </td>

                {/* Email Address */}
                <td className="px-6 py-5 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center space-x-2">
                    {lead.email ? (
                      <>
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <EnvelopeIcon className="h-4 w-4 text-purple-600" />
                        </div>
                        <a href={`mailto:${lead.email}`} className="text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors duration-200 truncate max-w-32">
                          {lead.email}
                        </a>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No email</span>
                    )}
                  </div>
                </td>

                {/* Service Type - for Other Services */}
                <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                  <div className="text-sm">
                    {lead.leadType === 'other_services' && lead.serviceType ? (
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${lead.serviceType === 'passport' ? 'bg-blue-100 text-blue-800' :
                        lead.serviceType === 'visa' ? 'bg-purple-100 text-purple-800' :
                          lead.serviceType === 'currency' ? 'bg-yellow-100 text-yellow-800' :
                            lead.serviceType === 'railway_ticketing' ? 'bg-red-100 text-red-800' :
                              lead.serviceType === 'bus_ticketing' ? 'bg-orange-100 text-orange-800' :
                                lead.serviceType === 'flight_ticketing' ? 'bg-green-100 text-green-800' :
                                  lead.serviceType === 'cruise_ticketing' ? 'bg-cyan-100 text-cyan-800' :
                                    lead.serviceType === 'car_rental' ? 'bg-indigo-100 text-indigo-800' :
                                      lead.serviceType === 'insurance' ? 'bg-pink-100 text-pink-800' :
                                        'bg-gray-100 text-gray-800'
                        }`}>
                        {lead.serviceType === 'passport' ? '🛂 Passport' :
                          lead.serviceType === 'visa' ? '📄 Visa' :
                            lead.serviceType === 'currency' ? '💱 Currency' :
                              lead.serviceType === 'railway_ticketing' ? '🚂 Railway' :
                                lead.serviceType === 'bus_ticketing' ? '🚌 Bus' :
                                  lead.serviceType === 'flight_ticketing' ? '✈️ Flight' :
                                    lead.serviceType === 'cruise_ticketing' ? '🚢 Cruise' :
                                      lead.serviceType === 'car_rental' ? '🚗 Car Rental' :
                                        lead.serviceType === 'insurance' ? '🛡️ Insurance' :
                                          '📦 ' + lead.serviceType}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </td>

                {/* Tour Package - only show if not filtering for other_services */}
                {leadTypeFilter !== 'other_services' && (
                  <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                    <div className="text-sm">
                      {lead.tourPackage ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          🎒 {lead.destination || 'Package'}
                          {lead.numberOfDays && <span className="ml-1">({lead.numberOfDays}D)</span>}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                )}

                {/* Travel Date - only show if not filtering for other_services */}
                {leadTypeFilter !== 'other_services' && (
                  <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                    <div className="text-sm text-gray-900">
                      {lead.travelDate ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                          📅 {new Date(lead.travelDate).toLocaleDateString('en-GB')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                )}

                {/* Added By Column */}
                <td className="px-6 py-4 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center space-x-2">
                    {lead.createdByName ? (
                      <>
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-teal-700">
                            {lead.createdByName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-800">{lead.createdByName}</span>
                          {lead.createdByDepartment && (
                            <span className="text-xs text-gray-500">{lead.createdByDepartment}</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Unknown</span>
                    )}
                  </div>
                </td>

                {/* Status Column with Update Functionality */}
                <td className="px-6 py-5 whitespace-nowrap border-r border-gray-300">
                  <div className="flex items-center justify-center">
                    {(() => {
                      const status = lead.status || (statusOptions.length > 0 ? statusOptions[0].value : 'new');

                      // Find current status option to get its label
                      const currentStatusOption = statusOptions.find(opt => opt.value === status);
                      const statusLabel = currentStatusOption?.label || status;

                      // Dynamic color assignment with high contrast text
                      const getStatusStyle = (statusValue) => {
                        // Enhanced color palette with darker text for better visibility
                        const colorMap = {
                          'new': {
                            bg: 'bg-yellow-100',
                            text: 'text-yellow-900 font-semibold',
                            border: 'border-yellow-400',
                            dot: 'bg-yellow-500'
                          },
                          'contacted': {
                            bg: 'bg-blue-100',
                            text: 'text-blue-900 font-semibold',
                            border: 'border-blue-400',
                            dot: 'bg-blue-500'
                          },
                          'qualified': {
                            bg: 'bg-indigo-100',
                            text: 'text-indigo-900 font-semibold',
                            border: 'border-indigo-400',
                            dot: 'bg-indigo-500'
                          },
                          'proposal_sent': {
                            bg: 'bg-purple-100',
                            text: 'text-purple-900 font-semibold',
                            border: 'border-purple-400',
                            dot: 'bg-purple-500'
                          },
                          'negotiation': {
                            bg: 'bg-orange-100',
                            text: 'text-orange-900 font-semibold',
                            border: 'border-orange-400',
                            dot: 'bg-orange-500'
                          },
                          'follow_up': {
                            bg: 'bg-cyan-100',
                            text: 'text-cyan-900 font-semibold',
                            border: 'border-cyan-400',
                            dot: 'bg-cyan-500'
                          },
                          'demo_scheduled': {
                            bg: 'bg-teal-100',
                            text: 'text-teal-900 font-semibold',
                            border: 'border-teal-400',
                            dot: 'bg-teal-500'
                          },
                          'quote_sent': {
                            bg: 'bg-pink-100',
                            text: 'text-pink-900 font-semibold',
                            border: 'border-pink-400',
                            dot: 'bg-pink-500'
                          },
                          'closed_won': {
                            bg: 'bg-green-100',
                            text: 'text-green-900 font-semibold',
                            border: 'border-green-400',
                            dot: 'bg-green-500'
                          },
                          'closed': {
                            bg: 'bg-green-100',
                            text: 'text-green-900 font-semibold',
                            border: 'border-green-400',
                            dot: 'bg-green-500'
                          },
                          'won': {
                            bg: 'bg-green-100',
                            text: 'text-green-900 font-semibold',
                            border: 'border-green-400',
                            dot: 'bg-green-500'
                          },
                          'closed_lost': {
                            bg: 'bg-red-100',
                            text: 'text-red-900 font-semibold',
                            border: 'border-red-400',
                            dot: 'bg-red-500'
                          },
                          'lost': {
                            bg: 'bg-red-100',
                            text: 'text-red-900 font-semibold',
                            border: 'border-red-400',
                            dot: 'bg-red-500'
                          },
                          'on_hold': {
                            bg: 'bg-gray-100',
                            text: 'text-gray-900 font-semibold',
                            border: 'border-gray-400',
                            dot: 'bg-gray-500'
                          },
                          'not_interested': {
                            bg: 'bg-slate-100',
                            text: 'text-slate-900 font-semibold',
                            border: 'border-slate-400',
                            dot: 'bg-slate-500'
                          },
                          'live': {
                            bg: 'bg-emerald-100',
                            text: 'text-emerald-900 font-semibold',
                            border: 'border-emerald-400',
                            dot: 'bg-emerald-500'
                          },
                          'quoted': {
                            bg: 'bg-violet-100',
                            text: 'text-violet-900 font-semibold',
                            border: 'border-violet-400',
                            dot: 'bg-violet-500'
                          }
                        };

                        // Try exact match first, then lowercase match, then default
                        return colorMap[statusValue] ||
                          colorMap[statusValue?.toLowerCase()] ||
                        {
                          bg: 'bg-gray-100',
                          text: 'text-gray-900 font-semibold',
                          border: 'border-gray-400',
                          dot: 'bg-gray-500'
                        };
                      };

                      const config = getStatusStyle(status);

                      return (
                        <div className="relative inline-block w-full max-w-xs">
                          <div className="flex items-center space-x-2">
                            {/* Status Indicator Dot */}
                            <span className={`flex-shrink-0 w-3 h-3 rounded-full ${config.dot}`}></span>

                            {/* Status Badge with Dropdown */}
                            <div className="relative">
                              <select
                                value={status}
                                onChange={(e) => {
                                  if (onStatusChange) {
                                    onStatusChange(lead._id || lead.id, e.target.value);
                                  }
                                }}
                                className={`
                                  text-xs px-3 py-2 border-2 rounded-lg font-bold
                                  transition-all duration-200 ease-in-out
                                  focus:ring-2 focus:ring-blue-300 focus:border-blue-500 focus:outline-none
                                  hover:shadow-md hover:border-blue-400
                                  cursor-pointer
                                  ${config.bg} ${config.text} ${config.border}
                                  disabled:opacity-50 disabled:cursor-not-allowed
                                  shadow-sm
                                `}
                                style={{
                                  minWidth: '140px',
                                  minHeight: '36px'
                                }}
                                disabled={loadingStatusOptions}
                              >
                                {loadingStatusOptions ? (
                                  <option value="">⏳ Loading...</option>
                                ) : (
                                  statusOptions.length > 0 ? (
                                    statusOptions.map(option => {
                                      const emoji = getStatusEmoji(option.value);
                                      const displayLabel = option.label || option.name || option.value || 'Unknown';
                                      return (
                                        <option key={option._id} value={option.value}>
                                          {emoji} {displayLabel}
                                        </option>
                                      );
                                    })
                                  ) : (
                                    <>
                                      <option value="new">🆕 New</option>
                                      <option value="contacted">📞 Contacted</option>
                                      <option value="qualified">✅ Qualified</option>
                                      <option value="proposal_sent">📋 Proposal Sent</option>
                                      <option value="negotiation">🤝 Negotiation</option>
                                      <option value="closed_won">🎉 Closed Won</option>
                                      <option value="closed_lost">❌ Closed Lost</option>
                                    </>
                                  )
                                )}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </td>

                {/* Actions Column */}
                <td className="px-6 py-5 whitespace-nowrap text-center">
                  <div className="flex justify-center space-x-2">
                    <button
                      onClick={() => onEdit && onEdit(lead)}
                      className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      title="Edit Lead"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete && onDelete(lead._id || lead.id)}
                      className="inline-flex items-center justify-center w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      title="Delete Lead"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    {onGenerateQuotation && (
                      <button
                        onClick={() => onGenerateQuotation(lead)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 hover:text-green-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                        title="Generate Quotation"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    )}
                    {/* <button
                      onClick={() => {
                        // Open catalog send modal for this lead
                        if (window.onSendCatalogToLead) {
                          window.onSendCatalogToLead(lead);
                        }
                      }}
                      className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 hover:bg-purple-200 text-purple-600 hover:text-purple-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      title="Send Catalog"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </button> */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <EnhancedLeadAssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedLeads([]);
          }}
          selectedLeads={selectedLeads}
          onAssignmentComplete={handleAssignmentComplete}
        />
      )}

      {/* Enquiry Purpose Modal */}
      {selectedLeadForPurpose && (
        <EnquiryPurposeModal
          isOpen={!!selectedLeadForPurpose}
          onClose={() => setSelectedLeadForPurpose(null)}
          lead={selectedLeadForPurpose}
        />
      )}

      {/* Customer Profile Modal */}
      {showCustomerProfile && selectedLeadId && (
        <CustomerProfileModal
          leadId={selectedLeadId}
          onClose={() => {
            setShowCustomerProfile(false);
            setSelectedLeadId(null);
          }}
        />
      )}
    </div>
  );
};

export default LeadList;
